"use client";

import { useState } from "react";
import { setComments } from "../../../store/ApplicationSlice";
import { Check, Trash2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { postNoteWithApplicationId } from "../../../api/notesService";

export const CommentPin = ({
  id,
  text,
  user,
  is_resolved,
  is_open,
  is_editing,
  style,
}) => {
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  const comments = useSelector((state) => state.intakeData.comments);
  const [input, setInput] = useState(text);

  const handleSave = async () => {
    const trimmed = input.trim();
    if (trimmed === "") {
      handleDelete();
    } else {
      const newComments = comments.map((c) =>
        c.id === id
          ? {
              ...c,
              text: trimmed,
              is_editing: false,
              is_resolved: c.is_resolved,
            }
          : c
      );
      const updatedComment = newComments.find((c) => c.id === id);
      const { id: _id, ...commentWithoutId } = updatedComment; // ✅ rename to avoid conflict
      dispatch(setComments(newComments));
      await postNoteWithApplicationId(
        commentWithoutId,
        accessToken,
        refreshToken
      );
    }
  };

  const handleResolve = () => {
    const newComments = comments.map((c) =>
      c.id === id ? { ...c, is_resolved: true } : c
    );
    dispatch(setComments(newComments));
  };

  const handleDelete = () => {
    const newComments = comments.filter((c) => c.id !== id);
    dispatch(setComments(newComments));
  };

  const handleToggleOpen = () => {
    const newComments = comments.map((c) =>
      c.id === id ? { ...c, is_open: !c.is_open } : c
    );
    dispatch(setComments(newComments));
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    const newComments = comments.map((c) =>
      c.id === id ? { ...c, is_open: false } : c
    );
    dispatch(setComments(newComments));
  };

  return (
    <div className={`absolute ${is_resolved ? "" : ""}`} style={style}>
      <div className="relative">
        {/* Avatar pin */}
        <div className="w-8 h-8 cursor-pointer" onClick={handleToggleOpen}>
          <div className="w-6 h-6 bg-white rounded-full shadow absolute top-1 left-1">
            <div
              className="w-full h-full rounded-full flex items-center justify-center text-white text-sm font-semibold"
              style={{ backgroundColor: user.color }}
            >
              {user.initials}
            </div>
          </div>
          <div className="absolute w-3 h-3 bg-white rotate-45 left-3 top-5 -z-[1] shadow" />
        </div>

        {/* Comment box */}
        {is_open && (
          <div className="absolute left-10 top-0 w-[300px] bg-white text-black rounded-xl shadow-lg p-3 comment-box border z-50">
            {/* Header */}
            <div className="flex justify-between items-center text-sm mb-2">
              <span className="font-semibold">Comment</span>
              <div className="flex gap-2">
                <button
                  onClick={handleResolve}
                  title="Resolve"
                  className="hover:bg-gray-100 p-1 rounded transition-colors"
                >
                  <Check className="w-4 h-4 text-green-600" />
                </button>
                <button
                  onClick={handleClose}
                  title="Close"
                  className="hover:bg-gray-100 p-1 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Text input on create or edit */}
            {is_editing ? (
              <textarea
                autoFocus
                placeholder="Write your comment..."
                className="w-full text-sm border rounded p-1 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={handleSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            ) : (
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex gap-2">
                  <Avatar user={user} />
                  <div>
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-sm">{text}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Avatar = ({ user }) => (
  <div
    className="min-w-[24px] h-[24px] rounded-full flex items-center justify-center text-xs font-bold text-white"
    style={{ backgroundColor: user.color }}
  >
    {user.initials}
  </div>
);
